import {
  useGetAllWorkspacesQuery,
  useGetUserSettingsQuery,
  useUpdateUserSettingsMutation,
} from '@carboncopyco/data';
import type { DataOptions, UnitOptions } from '@carboncopyco/types';
import {
  DropdownFilter,
  MultiDropdownFilter,
  PositionedToggle,
  TextBase,
  TextCaption,
  TextLedeOpening,
  notify,
} from '@carboncopyco/ui';
import { useParams } from 'react-router-dom';
import { WorkspaceFilter } from './WorkspaceFilter';

export const ActivitySummaryReport = () => {
  const { organizationId } = useParams();
  const { data: notificationsSettings } = useGetUserSettingsQuery({
    organizationId,
  });
  const [updateNotificationsSettings] = useUpdateUserSettingsMutation();
  const activityReport = notificationsSettings?.activityReport;

  const dataCoverageOptions: DataOptions[] =
    activityReport?.dataCoverage?.options;

  const optionsSet = new Set<DataOptions>(dataCoverageOptions);

  const { data: workspaces } = useGetAllWorkspacesQuery({
    organizationId,
    removeDeleted: false,
  });

  const dataCoverageList = [
    { id: 'demo', label: 'Demo activity' },
    { id: 'sandbox', label: 'Sandbox activity' },
    { id: 'tour', label: 'Tour activity' },
  ];

  const handleSelectedDataCoverage = (dataOption: DataOptions) => {
    optionsSet.has(dataOption)
      ? optionsSet.delete(dataOption)
      : optionsSet.add(dataOption);

    const selectedDataOptions = Array.from(optionsSet.values());

    updateNotificationsSettings({
      ...notificationsSettings,
      activityReport: {
        ...activityReport,
        dataCoverage: {
          options: selectedDataOptions,
          selectionMode: selectedDataOptions.length ? 'custom' : 'all',
        },
      },
    })
      .unwrap()
      .catch(() => {
        notify.error("Settings didn't update. Please try again.");
      });
  };

  const handleClearDataCoverage = () => {
    updateNotificationsSettings({
      ...notificationsSettings,
      activityReport: {
        ...activityReport,
        dataCoverage: {
          options: [],
          selectionMode: 'all',
        },
      },
    })
      .unwrap()
      .catch(() => {
        notify.error("Settings didn't update. Please try again.");
      });
  };

  return (
    notificationsSettings && (
      <div className="w-full flex flex-col gap-8">
        <TextLedeOpening
          label="Activity summary report"
          color="text-ds-natural-600"
        />
        <div className="flex justify-between">
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <PositionedToggle
                checked={activityReport.enabled}
                name="organizationActivity"
                onChange={(value) => {
                  updateNotificationsSettings({
                    ...notificationsSettings,
                    activityReport: {
                      ...activityReport,
                      enabled: value.target.checked,
                    },
                  })
                    .unwrap()
                    .catch(() => {
                      notify.error("Settings didn't update. Please try again.");
                    });
                }}
              />
              <TextBase
                label="Activity summary report"
                color="text-ds-natural-600"
              />
            </div>
            <div className="pl-8">
              <TextCaption
                label={
                  activityReport.enabled
                    ? "You're subscribed for the report!"
                    : 'Opt in for receiving the report'
                }
                color="text-ds-natural-600"
              />
            </div>
          </div>
          <div className="flex gap-7">
            <div className="flex flex-col gap-1.5">
              <TextCaption
                label="Workspaces"
                color="text-ds-natural-400"
              />
              {workspaces && <WorkspaceFilter workspaces={workspaces} />}
            </div>
            <div className="flex flex-col gap-1.5">
              <TextCaption
                label="Data scope"
                color="text-ds-natural-400"
              />

              <MultiDropdownFilter
                placeholder="Include all data"
                onChange={handleSelectedDataCoverage}
                selectedItems={
                  dataCoverageOptions
                    ? Object.fromEntries(
                        dataCoverageOptions.map((optionId) => [
                          optionId,
                          dataCoverageList.find((option) => {
                            return option.id === optionId;
                          })?.label,
                        ])
                      )
                    : {}
                }
                contentWidth={216}
                capsuleWidth={216}
                rounded={false}
                onClear={() => {
                  handleClearDataCoverage();
                }}
                fixedGroup={{ items: dataCoverageList }}
                disabled={!activityReport.enabled}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <TextCaption
                label="Delivery frequency"
                color="text-ds-natural-400"
              />
              <DropdownFilter
                disabled={!activityReport.enabled}
                rounded={false}
                capsuleWidth={216}
                contentWidth={216}
                items={[
                  { label: 'Weekly', id: 'week' },
                  { label: 'Monthly', id: 'month' },
                ]}
                onSelect={(id: UnitOptions) => {
                  updateNotificationsSettings({
                    ...notificationsSettings,
                    activityReport: {
                      ...activityReport,
                      interval: { value: 1, unit: id },
                    },
                  })
                    .unwrap()
                    .catch(() => {
                      notify.error("Settings didn't update. Please try again.");
                    });
                }}
                selected={activityReport.interval.unit as UnitOptions}
                placeholder="Delivery frequency"
              />
            </div>
          </div>
        </div>
      </div>
    )
  );
};
